import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import WorldCupTrophy from './assets/world_cup_trophy.png';
import Logo from './assets/logo-bolao-top-cropped.png';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

// --- Helpers de Pontuação (Duplicados do App.js para independência da Landing Page) ---
const getPhaseSortValue = (phase) => {
  if (!phase) return 500;
  
  const cleanPhase = phase.trim().toLowerCase();
  
  if (cleanPhase.startsWith('rodada ')) {
    return parseInt(cleanPhase.split(' ')[1], 10) || 0;
  }
  switch (cleanPhase) {
    case '16 avos':
    case '16-avos':
      return 50;
    case 'oitavas de final':
    case 'oitavas':
      return 100;
    case 'quartas de final':
    case 'quartas':
      return 200;
    case 'semi final':
    case 'semifinal':
      return 300;
    case 'disputa de 3º lugar':
    case 'disputa 3º lugar':
    case '3º lugar':
    case 'terceiro lugar':
      return 350;
    case 'final':
      return 400;
    case 'jogos gerais':
      return -1;
    default:
      return 500;
  }
};

const getPointsInfo = (bet, result, pool = null) => {
    if (!bet || result.resultA === null || result.resultB === null) return { points: 0 };
    const betA = parseInt(bet.betA, 10);
    const betB = parseInt(bet.betB, 10);
    if (isNaN(betA) || isNaN(betB)) return { points: 0 };
    const resultA = result.resultA;
    const resultB = result.resultB;
    let rawPoints = 0;
    if (betA === resultA && betB === resultB) {
        rawPoints = 10;
    } else {
        const betWinner = betA > betB ? 'A' : betA < betB ? 'B' : 'D';
        const resultWinner = resultA > resultB ? 'A' : resultA < resultB ? 'B' : 'D';
        if (betWinner === resultWinner && betWinner !== 'D') {
            if ((betWinner === 'A' && betA === resultA) || (betWinner === 'B' && betB === resultB)) {
                rawPoints = 6;
            } else {
                rawPoints = 3;
            }
        } else if (resultWinner === 'D' && betWinner === 'D') {
            rawPoints = 5;
        }
    }
    
    let finalPoints = bet.isBonus ? rawPoints * 2 : rawPoints;
    
    if (pool && pool.knockoutMultiplierPhase && pool.knockoutMultiplierPhase !== 'Nenhuma') {
        const matchPhaseValue = getPhaseSortValue(result.phase);
        const multiplierPhaseValue = getPhaseSortValue(pool.knockoutMultiplierPhase);
        
        if (matchPhaseValue >= 50 && matchPhaseValue <= 400 && matchPhaseValue >= multiplierPhaseValue) {
            finalPoints *= 2;
        }
    }
    
    return { points: finalPoints };
};

const getF1PointsInfo = (bet, match) => {
    if (!bet || !match.resultP1 || !match.resultP2 || !match.resultP3) return { points: 0 };
    let points = 0;
    let hits = 0;
    if (bet.p1 === match.resultP1) { points += 10; hits++; }
    if (bet.p2 === match.resultP2) { points += 6; hits++; }
    if (bet.p3 === match.resultP3) { points += 3; hits++; }
    if (hits === 3) points += 1;
    return { points };
};
// -----------------------------------------------------------------------------------

const PublicPoolModal = ({ pool, onClose, onJoin }) => {
    const { t } = useTranslation();
    const [ranking, setRanking] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Buscar Participantes (já estão no objeto pool, mas apenas nomes)
                // 2. Buscar Campeonato (para saber tipo e filtrar jogos)
                // 3. Buscar Jogos (para calcular pontos)
                // 4. Buscar Palpites (subcoleção 'bets')

                // Buscar Campeonato
                const champRef = doc(db, "championships", pool.championshipId);
                const champSnap = await getDoc(champRef);
                const champData = champSnap.exists() ? champSnap.data() : null;

                // Buscar Jogos
                const matchesQ = query(collection(db, "matches"), where("championshipId", "==", pool.championshipId));
                const matchesSnap = await getDocs(matchesQ);
                const matches = matchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                const finishedMatches = matches.filter(m => m.status === 'finished');

                // Buscar Palpites de todos os participantes
                const betsRef = collection(db, "pools", pool.id, "bets");
                const betsSnap = await getDocs(betsRef);
                const allBets = {}; // { uid: { matchId: betData } }
                betsSnap.forEach(doc => {
                    allBets[doc.id] = doc.data();
                });

                // Calcular Ranking
                const calculatedRanking = (pool.participants || []).map(p => {
                    const userBets = allBets[p.uid] || {};
                    let totalPoints = 0;

                    finishedMatches.forEach(match => {
                        const bet = userBets[match.id];
                        if (bet) {
                            if (match.type === 'f1_race') {
                                totalPoints += getF1PointsInfo(bet, match).points;
                            } else {
                                totalPoints += getPointsInfo(bet, match, pool).points;
                            }
                        }
                    });

                    // Bônus de Temporada (F1) - Simplificado para Landing Page (não mostra até acabar)
                    // Se o campeonato já tiver campeão definido, somar
                    if (champData?.championDriver && userBets.seasonBets?.championDriver === champData.championDriver) totalPoints += 25;
                    if (champData?.championTeam && userBets.seasonBets?.championTeam === champData.championTeam) totalPoints += 25;


                    return { ...p, points: totalPoints };
                });

                calculatedRanking.sort((a, b) => {
                    if (b.points !== a.points) return b.points - a.points;
                    return (a.displayName || "").localeCompare(b.displayName || "");
                });
                setRanking(calculatedRanking);

            } catch (error) {
                console.error("Erro ao carregar dados do bolão:", error);
            } finally {
                setLoading(false);
            }
        };

        if (pool) fetchData();
    }, [pool]);

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
            <div className="bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-700 overflow-hidden flex flex-col max-h-[80vh]">
                <div className="p-6 border-b border-slate-700 bg-slate-900/50 flex justify-between items-center invert-0">
                    <div>
                        <h3 className="text-xl font-bold text-white">{pool.name}</h3>
                        <p className="text-slate-400 text-sm">{t(pool.championshipName) || t('championship')}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="p-0 overflow-y-auto flex-grow custom-scrollbar">
                    {pool.photoUrl && (
                        <div className="w-full h-40 bg-slate-700 relative">
                            <img src={pool.photoUrl} alt={pool.name} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-800 to-transparent"></div>
                        </div>
                    )}

                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="font-bold text-green-400 uppercase text-xs tracking-wider">{t('currentRanking')}</h4>
                            <span className="text-xs text-slate-500">{ranking.length} {t('participants')}</span>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-8"><span className="animate-spin text-2xl">⏳</span></div>
                        ) : (
                            <ul className="space-y-2">
                                {ranking.map((p, idx) => (
                                    <li key={p.uid} className="flex justify-between items-center bg-slate-700/50 p-3 rounded-lg border border-slate-700/50">
                                        <div className="flex items-center gap-3">
                                            <span className={`font-black w-6 text-center ${idx < 3 ? 'text-yellow-400' : 'text-slate-500'}`}>{idx + 1}º</span>
                                            <span className="text-slate-200 font-medium">{p.displayName || t('participant')}</span>
                                        </div>
                                        <span className="font-bold text-green-400">{p.points} pts</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="p-6 border-t border-slate-700 bg-slate-900/50 text-center">
                    <button
                        onClick={() => onJoin(pool.id)}
                        className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg transform hover:scale-[1.02] transition-all"
                    >
                        {t('joinNow')}
                    </button>
                    <p className="text-xs text-slate-500 mt-3">{t('loginRequiredToJoin')}</p>
                </div>
            </div>
        </div>
    );
};

const LandingPage = ({ onLoginClick }) => {
    const { t } = useTranslation();
    const [publicPools, setPublicPools] = useState([]);
    const [selectedPool, setSelectedPool] = useState(null);
    const [activeVideoId, setActiveVideoId] = useState(null);

    const tutorialVideos = [
        { id: 'kuP4d4UT8xs', titleKey: 'tutorial1Title' },
        { id: 'pTKJqfePWpQ', titleKey: 'tutorial2Title' },
        { id: 'hIoPtY3pq1A', titleKey: 'tutorial3Title' },
    ];

    useEffect(() => {
        const fetchPublicPools = async () => {
            try {
                // Busca bolões públicos
                const q = query(collection(db, "pools"), where("isPublic", "==", true));
                const querySnapshot = await getDocs(q);
                const pools = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // Busca nomes e status dos campeonatos para exibir nos cards e filtrar
                // Otimização: Buscar únicos IDs de campeonatos
                const champIds = [...new Set(pools.map(p => p.championshipId))];
                const champsMap = {};
                const champsStatusMap = {};

                for (const champId of champIds) {
                    if (champId) {
                        const champDoc = await getDoc(doc(db, "championships", champId));
                        if (champDoc.exists()) {
                            const champData = champDoc.data();
                            champsMap[champId] = champData.name;
                            champsStatusMap[champId] = champData.status;
                        }
                    }
                }

                // Filtra para exibir apenas campeonatos ativos e anexa o nome
                const poolsWithChamps = pools
                    .filter(p => champsStatusMap[p.championshipId] === 'active')
                    .map(p => ({
                        ...p,
                        championshipName: champsMap[p.championshipId] || 'Campeonato'
                    }));

                setPublicPools(poolsWithChamps);
            } catch (error) {
                console.error("Erro ao buscar bolões públicos:", error);
            }
        };

        fetchPublicPools();
    }, []);

    const handleOpenPool = (pool) => {
        setSelectedPool(pool);
    };

    const handleJoinPool = (poolId) => {
        // Atualiza a URL com o parâmetro de join sem recarregar a página
        const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + '?join=' + poolId;
        window.history.pushState({ path: newUrl }, '', newUrl);

        // fecha modal
        setSelectedPool(null);

        // Abre o login (App.js vai detectar o ?join depois do login)
        onLoginClick();
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="flex flex-col min-h-screen text-slate-200 font-sans" style={{ backgroundColor: '#0f172a' }}>
            {/* Navigation */}
            <nav className="sticky top-0 z-50 w-full border-b border-slate-700 shadow-lg" style={{ background: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(10px)', paddingTop: 'env(safe-area-inset-top)' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-20">
                        {/* Logo Area */}
                        <div className="flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={() => window.scrollTo(0, 0)}>
                            <img src={Logo} alt="Bolões Top" className="h-16 w-auto" />
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center gap-6 ml-10">
                            <button onClick={() => scrollToSection('como-funciona')} className="hover:text-yellow-400 transition-colors px-3 py-2 rounded-md text-sm font-medium bg-transparent border-none cursor-pointer">{t('howItWorks')}</button>
                            <button onClick={() => scrollToSection('criar-bolao')} className="hover:text-yellow-400 transition-colors px-3 py-2 rounded-md text-sm font-medium bg-transparent border-none cursor-pointer">{t('privatePool')}</button>
                            <LanguageSwitcher />
                        </div>

                        {/* CTA Button & Mobile Language */}
                        <div className="flex items-center gap-2 sm:gap-4">
                            <div className="md:hidden">
                                <LanguageSwitcher />
                            </div>
                            <button
                                onClick={onLoginClick}
                                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-bold py-1.5 px-4 sm:py-2 sm:px-6 text-xs sm:text-base rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 ring-2 ring-green-600/50 whitespace-nowrap"
                            >
                                <span className="hidden sm:inline">{t('loginOrCreate')}</span>
                                <span className="sm:hidden">{t('login')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative overflow-hidden bg-slate-900 flex items-center justify-center min-h-[80vh]">
                {/* Background Decorative Elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
                    <div className="absolute -top-24 -left-24 w-96 h-96 bg-green-600 rounded-full blur-3xl mix-blend-screen"></div>
                    <div className="absolute top-1/2 -right-24 w-72 h-72 bg-yellow-500 rounded-full blur-3xl mix-blend-screen"></div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-[800px] h-[400px] bg-slate-800 rounded-full blur-3xl opacity-50"></div>
                </div>

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 text-center">
                    <div className="space-y-8 animate-fade-in">
                        <div className="inline-flex items-center px-3 py-1 rounded-full border border-green-500/30 bg-green-500/10 text-green-400 text-sm font-semibold mb-4">
                            <span className="mr-2">🏆</span> {t('mostCompletePlatform')}
                        </div>

                        <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight leading-tight uppercase">
                            {t('soccerAndF1')} <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400 uppercase whitespace-nowrap">{t('f1')}</span>
                        </h1>

                        <p className="text-xl md:text-2xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
                            {t('heroDescription1')}
                            <strong className="text-white"> {t('soccer')}</strong>{t('heroDescription2')}
                            <strong className="text-white whitespace-nowrap"> {t('f1')}</strong>.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
                            <button
                                onClick={onLoginClick}
                                className="flex items-center justify-center px-10 py-5 border border-transparent text-lg font-bold rounded-xl text-slate-900 bg-yellow-400 hover:bg-yellow-300 shadow-[0_0_20px_rgba(250,204,21,0.3)] transition-all transform hover:-translate-y-1"
                            >
                                {t('createFreeAccount')}
                            </button>
                            <a
                                href="https://play.google.com/store/apps/details?id=br.com.boloestop.bolao"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center px-8 py-4 border border-slate-600 hover:border-green-400 text-white bg-slate-800/80 hover:bg-slate-700/80 backdrop-blur-sm rounded-xl shadow-[0_0_20px_rgba(0,0,0,0.3)] transition-all transform hover:-translate-y-1 group"
                            >
                                <svg className="w-8 h-8 mr-3 text-green-500 group-hover:text-green-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.6,9.48l1.84-3.18c0.16-0.31,0.04-0.69-0.26-0.85c-0.29-0.15-0.65-0.06-0.83,0.22l-1.88,3.24 c-2.86-1.21-6.08-1.21-8.94,0L5.65,5.67C5.46,5.39,5.11,5.3,4.81,5.46C4.51,5.62,4.39,6,4.56,6.3l1.84,3.18 C3.49,11.02,1.6,14.49,1.19,18.43h21.62C22.4,14.49,20.51,11.02,17.6,9.48z M7.68,15.75c-0.65,0-1.18-0.53-1.18-1.18 c0-0.65,0.53-1.18,1.18-1.18s1.18,0.53,1.18,1.18C8.86,15.22,8.33,15.75,7.68,15.75z M16.32,15.75c-0.65,0-1.18-0.53-1.18-1.18 c0-0.65,0.53-1.18,1.18-1.18s1.18,0.53,1.18,1.18C17.5,15.22,16.97,15.75,16.32,15.75z"></path>
                                </svg>
                                <div className="flex flex-col items-start text-left">
                                    <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80 leading-none mb-1 text-slate-300">{t('availableOn')}</span>
                                    <span className="text-lg leading-none font-bold text-white tracking-wide">{t('googlePlay')}</span>
                                </div>
                            </a>
                        </div>


                    </div>

                    {/* --- PUBLIC POOLS SECTION --- */}
                    {publicPools.length > 0 && (
                        <div className="mt-20 w-full animate-fade-in-up">
                            <div className="flex items-center justify-center gap-2 mb-8">
                                <div className="h-[1px] w-12 bg-slate-700"></div>
                                <h2 className="text-xl font-bold text-slate-300 uppercase tracking-widest">{t('publicPools')}</h2>
                                <div className="h-[1px] w-12 bg-slate-700"></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                                {publicPools.map(pool => (
                                    <div
                                        key={pool.id}
                                        onClick={() => handleOpenPool(pool)}
                                        className="group bg-slate-800 rounded-2xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-green-400 transition-all duration-300 transform hover:-translate-y-1 shadow-lg relative"
                                    >
                                        <div className="h-40 bg-slate-700 relative overflow-hidden">
                                            {pool.photoUrl ? (
                                                <img src={pool.photoUrl} alt={pool.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800 text-6xl">🏆</div>
                                            )}
                                            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors"></div>
                                        </div>
                                        <div className="p-6">
                                            <span className="text-xs font-bold text-green-400 uppercase tracking-wider mb-2 block">{t(pool.championshipName)}</span>
                                            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-green-300 transition-colors">{pool.name}</h3>
                                            <div className="flex items-center text-slate-400 text-sm">
                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                                                {(pool.participants || []).length} {t('participants')}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* --------------------------- */}

                </div>
            </header>

            {/* How It Works Section */}
            <section id="como-funciona" className="py-20 bg-slate-800 relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-base text-green-400 font-semibold tracking-wide uppercase">{t('simpleAndFast')}</h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-white sm:text-4xl">
                            {t('howItWorksQuestion')}
                        </p>
                        <p className="mt-4 max-w-2xl text-xl text-slate-400 mx-auto">
                            {t('howItWorksSubtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
                        {/* Step 1 */}
                        <div className="p-8 rounded-2xl hover:bg-slate-700/50 transition-colors border-t-4 border-green-500 relative" style={{ background: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-slate-900 border-4 border-green-500 flex items-center justify-center text-xl font-bold text-white">1</div>
                            <div className="text-center mt-6">
                                <div className="text-4xl mb-4">👤</div>
                                <h3 className="text-lg font-bold text-white mb-2">{t('step1Title')}</h3>
                                <p className="text-slate-400 text-sm">{t('step1Desc')}</p>
                            </div>
                        </div>

                        {/* Step 2 */}
                        <div className="p-8 rounded-2xl hover:bg-slate-700/50 transition-colors border-t-4 border-yellow-500 relative" style={{ background: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-slate-900 border-4 border-yellow-500 flex items-center justify-center text-xl font-bold text-white">2</div>
                            <div className="text-center mt-6">
                                <div className="text-4xl mb-4">🏆</div>
                                <h3 className="text-lg font-bold text-white mb-2">{t('step2Title')}</h3>
                                <p className="text-slate-400 text-sm">{t('step2Desc')}</p>
                            </div>
                        </div>

                        {/* Step 3 */}
                        <div className="p-8 rounded-2xl hover:bg-slate-700/50 transition-colors border-t-4 border-blue-500 relative" style={{ background: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-slate-900 border-4 border-blue-500 flex items-center justify-center text-xl font-bold text-white">3</div>
                            <div className="text-center mt-6">
                                <div className="text-4xl mb-4">🎯</div>
                                <h3 className="text-lg font-bold text-white mb-2">{t('step3Title')}</h3>
                                <p className="text-slate-400 text-sm">{t('step3Desc')}</p>
                            </div>
                        </div>

                        {/* Step 4 */}
                        <div className="p-8 rounded-2xl hover:bg-slate-700/50 transition-colors border-t-4 border-purple-500 relative" style={{ background: 'rgba(30, 41, 59, 0.7)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-slate-900 border-4 border-purple-500 flex items-center justify-center text-xl font-bold text-white">4</div>
                            <div className="text-center mt-6">
                                <div className="text-4xl mb-4">🎉</div>
                                <h3 className="text-lg font-bold text-white mb-2">{t('step4Title')}</h3>
                                <p className="text-slate-400 text-sm">{t('step4Desc')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video Tutorials Section */}
            <section id="tutoriais" className="py-20 bg-slate-900 border-t border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-white mb-4">{t('tutorialsTitle')}</h2>
                        <p className="text-slate-400">{t('tutorialsSubtitle')}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {tutorialVideos.map((video) => (
                            <div 
                                key={video.id} 
                                className="group relative rounded-2xl overflow-hidden cursor-pointer bg-slate-800 border border-slate-700 hover:border-green-500/50 transition-all shadow-lg hover:shadow-green-500/10"
                                onClick={() => setActiveVideoId(video.id)}
                            >
                                <div className="aspect-video relative overflow-hidden bg-slate-900">
                                    <img 
                                        src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} 
                                        alt={t(video.titleKey)} 
                                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                    />
                                    {/* Play Button Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-16 h-16 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center border-2 border-white/20 group-hover:bg-green-600 group-hover:border-green-500 group-hover:scale-110 transition-all duration-300 shadow-xl">
                                            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-5">
                                    <h3 className="text-lg font-bold text-slate-200 group-hover:text-green-400 transition-colors">
                                        {t(video.titleKey)}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Private Pool Call to Action */}
            <section id="criar-bolao" className="py-20 relative overflow-hidden">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="bg-gradient-to-r from-green-900 to-slate-900 rounded-3xl p-8 md:p-16 shadow-2xl border border-green-700/30 text-center">
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                            {t('createPrivatePoolFree')} <span className="text-yellow-400">{t('privatePoolFreeHighlight')}</span>
                        </h2>
                        <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
                            {t('privatePoolDesc')}
                        </p>

                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <button
                                onClick={onLoginClick}
                                className="bg-white text-green-900 hover:bg-slate-100 font-bold py-4 px-10 rounded-xl shadow-lg transition-transform transform hover:-translate-y-1"
                            >
                                {t('createPoolNow')}
                            </button>
                            <a
                                href="https://play.google.com/store/apps/details?id=br.com.boloestop.bolao"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center px-8 py-3 border border-green-700 hover:border-green-400 text-white bg-slate-900/50 hover:bg-slate-800/80 rounded-xl shadow-lg transition-all transform hover:-translate-y-1 group"
                            >
                                <svg className="w-8 h-8 mr-3 text-green-500 group-hover:text-green-400 transition-colors" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.6,9.48l1.84-3.18c0.16-0.31,0.04-0.69-0.26-0.85c-0.29-0.15-0.65-0.06-0.83,0.22l-1.88,3.24 c-2.86-1.21-6.08-1.21-8.94,0L5.65,5.67C5.46,5.39,5.11,5.3,4.81,5.46C4.51,5.62,4.39,6,4.56,6.3l1.84,3.18 C3.49,11.02,1.6,14.49,1.19,18.43h21.62C22.4,14.49,20.51,11.02,17.6,9.48z M7.68,15.75c-0.65,0-1.18-0.53-1.18-1.18 c0-0.65,0.53-1.18,1.18-1.18s1.18,0.53,1.18,1.18C8.86,15.22,8.33,15.75,7.68,15.75z M16.32,15.75c-0.65,0-1.18-0.53-1.18-1.18 c0-0.65,0.53-1.18,1.18-1.18s1.18,0.53,1.18,1.18C17.5,15.22,16.97,15.75,16.32,15.75z"></path>
                                </svg>
                                <div className="flex flex-col items-start text-left">
                                    <span className="text-[10px] uppercase tracking-wider font-semibold opacity-80 leading-none mb-1 text-slate-300">{t('availableOn')}</span>
                                    <span className="text-lg leading-none font-bold text-white tracking-wide">{t('googlePlay')}</span>
                                </div>
                            </a>
                        </div>

                        {/* Feature List */}
                        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-medium text-slate-400">
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-green-500">✓</span> {t('featureFree')}
                            </div>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-green-500">✓</span> {t('featureInviteLink')}
                            </div>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-green-500">✓</span> {t('featureAutoRanking')}
                            </div>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-green-500">✓</span> {t('featureSoccerAndF1')}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="py-20 bg-slate-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-white mb-4">{t('faqTitle')}</h2>
                        <p className="text-slate-400">{t('faqSubtitle')}</p>
                    </div>

                    <div className="space-y-6">
                        {[
                            { q: t('faqQ1'), a: t('faqA1') },
                            { q: t('faqQ2'), a: t('faqA2') },
                            { q: t('faqQ3'), a: t('faqA3') },
                            { q: t('faqQ4'), a: t('faqA4') },
                            { q: t('faqQ5'), a: t('faqA5') }
                        ].map((item, i) => (
                            <div key={i} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                                <h3 className="text-lg font-bold text-teal-400 mb-2">{item.q}</h3>
                                <p className="text-slate-300 leading-relaxed text-sm sm:text-base">{item.a}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* FAQ Structured Data for SEO */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": t('faqQ1'),
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": t('faqA1')
                                }
                            },
                            {
                                "@type": "Question",
                                "name": t('faqQ2'),
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": t('faqA2')
                                }
                            }
                        ]
                    })}
                </script>
            </section>

            {/* Footer */}
            <footer className="bg-slate-950 border-t border-slate-800 pt-12 pb-8 mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        {/* Brand */}
                        <div className="col-span-1">
                            <span className="text-2xl font-black text-white">BOLÕES <span className="text-yellow-400">TOP</span></span>
                            <p className="mt-4 text-slate-500 text-sm">
                                {t('footerDesc')}
                            </p>
                        </div>

                        {/* Links */}
                        <div className="col-span-1">
                            <h4 className="text-white font-bold mb-4">{t('quickLinks')}</h4>
                            <ul className="space-y-2 text-slate-400 text-sm">
                                <li><button onClick={onLoginClick} className="hover:text-green-400 bg-transparent border-none cursor-pointer p-0 text-left">{t('login')}</button></li>
                                <li><button onClick={onLoginClick} className="hover:text-green-400 bg-transparent border-none cursor-pointer p-0 text-left">{t('createAccount')}</button></li>
                            </ul>
                        </div>

                        {/* Contact */}
                        <div className="col-span-1">
                            <h4 className="text-white font-bold mb-4">{t('contact')}</h4>
                            <ul className="space-y-2 text-slate-400 text-sm">
                                <li className="flex items-center gap-2">📧 contato@boloestop.com.br</li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-600">
                        <p dangerouslySetInnerHTML={{ __html: t('copyright') }}></p>
                        <div className="flex gap-4 mt-2 md:mt-0 flex-wrap justify-center">
                            <a href="https://boloestop.com.br/politica-privacidade" className="hover:text-green-400 transition-colors">{t('privacyPolicy')}</a>
                            <a href="https://boloestop.com.br/termos-servico" className="hover:text-green-400 transition-colors">{t('termsOfService')}</a>
                            <a href="https://boloestop.com.br/regras-bolao-online-gratis" className="hover:text-green-400 transition-colors">{t('poolRules')}</a>
                            <a href="https://boloestop.com.br/criar-organizar-bolao-gratis" className="hover:text-green-400 transition-colors">{t('howToOrganize')}</a>
                            <a href="https://boloestop.com.br/bolao-copa-2026" className="hover:text-green-400 transition-colors">{t('worldCupGuide')}</a>
                        </div>
                    </div>
                </div>
            </footer>


            {/* Public Pool Modal */}
            {
                selectedPool && (
                    <PublicPoolModal
                        pool={selectedPool}
                        onClose={() => setSelectedPool(null)}
                        onJoin={handleJoinPool}
                    />
                )
            }

            {/* Video Tutorial Modal (Lightbox) */}
            {activeVideoId && (
                <div 
                    className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fade-in" 
                    onClick={() => setActiveVideoId(null)}
                >
                    <div 
                        className="relative w-full max-w-4xl bg-slate-900 rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden border border-slate-700" 
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button 
                            onClick={() => setActiveVideoId(null)} 
                            className="absolute top-4 right-4 z-10 text-white/70 hover:text-white bg-black/50 hover:bg-black/80 rounded-full p-2 transition-all"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="aspect-video w-full bg-black">
                            <iframe 
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&rel=0`}
                                title="YouTube video player" 
                                frameBorder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                                allowFullScreen>
                            </iframe>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default LandingPage;
